import { motion, useReducedMotion } from "motion/react";
import { Compass } from "@phosphor-icons/react";
import { whyLetterContent } from "../../content";
import { FounderSignatures } from "../ui/FounderSignatures";
import { Button } from "../ui/Button";
import { CaptainPatchImage } from "../brand/AutterBrand";

const EASE = [0.16, 1, 0.3, 1] as const;

export function WhyLetter({
  onJoin,
  onPlay,
  onReadMore,
}: {
  onJoin?: () => void;
  onPlay?: () => void;
  onReadMore?: () => void;
}) {
  const reduce = useReducedMotion();
  const { hook, paragraphs, signoffNote } = whyLetterContent;

  return (
    <section id="why" className="relative bg-paper">
      <div className="relative mx-auto w-full max-w-[46rem] px-5 py-20 md:px-6 md:py-28">
        {/* ── The note, read as one continuous message ── */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono text-[10px] tracking-[0.14em] text-signal-deep uppercase">{hook.kicker}</p>
          <h2 className="display-type mt-3 font-black text-[clamp(3rem,9vw,7rem)] tracking-[-0.03em] text-ink">{hook.title}</h2>

          {onJoin && (
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-ink bg-foam px-5 py-4 shadow-card">
              <p className="text-base font-semibold text-ink md:text-lg">
                Be a part of it.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {onReadMore && (
                  <button
                    type="button"
                    onClick={onReadMore}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border-2 border-ink px-5 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-ink hover:text-foam"
                  >
                    Read more
                  </button>
                )}
                <Button href="#apply" size="md" arrow="right" onClick={onJoin}>
                  Join now
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mt-10 space-y-6"
        >
          {hook.intro.map((line, i) => (
            <p key={i} className="text-xl leading-relaxed font-medium text-ink-soft md:text-2xl">
              {line}
            </p>
          ))}
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-12"
        >
          <div className="space-y-6">
            {paragraphs.map((paragraph, i) => (
              <p key={i} className="text-xl leading-relaxed font-medium text-ink-soft md:text-2xl">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10">
            <FounderSignatures />
            <p className="mt-1 font-mono text-xs text-muted">{signoffNote}</p>
          </div>

          {onJoin && (
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mt-16 flex flex-col items-center gap-5 text-center"
            >
              <Button href="#apply" size="lg" arrow="right" onClick={onJoin}>
                Join now
              </Button>

              {onPlay && (
                <button
                  type="button"
                  onClick={onPlay}
                  className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-muted uppercase transition-colors hover:text-ink"
                >
                  <Compass className="size-4" weight="bold" aria-hidden />
                  or play the harbour first
                </button>
              )}
            </motion.div>
          )}
        </motion.div>

        <div className="mt-14 flex justify-center">
          <CaptainPatchImage pose="map" className="h-28 w-28 rotate-3 drop-shadow-xl md:h-36 md:w-36" />
        </div>
      </div>
    </section>
  );
}
